import { useState, useRef, useEffect } from "react"
import { useTareas } from "../context/TareasContext"
import Modal from "../../ui/Modal"
import { useSelector } from "react-redux"
import { toast } from "react-toastify"
import {
  FaCheck, FaUser, FaPlus, FaMicrophone,
  FaStop, FaTrash, FaExternalLinkAlt, FaTimes, FaChevronDown
} from "react-icons/fa"
import { MdSchedule } from "react-icons/md"
import InputFormValue from "../../ui/components/InputFormValue"
import MainSaveButton from "../../ui/components/MainSaveButton"
import formatWorkerDisplayName from "../utils/formatWorkerDisplayName"
import ReactQuill, { Quill } from "react-quill"
import "react-quill/dist/quill.snow.css"

const PRIORITIES = ["Baja", "Media", "Alta", "Urgente"]

const normalizeToDelta = (content) => {
  if (!content) return { ops: [{ insert: "\n" }] }
  if (typeof content === "object" && content.ops && Array.isArray(content.ops)) return content
  if (typeof content === "string") {
    try {
      const tempQuill = new Quill(document.createElement("div"))
      tempQuill.clipboard.dangerouslyPasteHTML(content)
      const delta = tempQuill.getContents()
      if (delta?.ops?.length > 0) return delta
      const Delta = Quill.import("delta")
      return new Delta().insert(content + "\n")
    } catch {
      const Delta = Quill.import("delta")
      return new Delta().insert(content + "\n")
    }
  }
  return { ops: [{ insert: "\n" }] }
}

const workerAreaPuesto = (w) => {
  const area = typeof w?.area === "string" ? w.area : w?.area?.name
  const puesto = typeof w?.puesto === "string" ? w.puesto : w?.puesto?.name
  const parts = [area, puesto].filter(Boolean)
  return parts.length ? parts.join(" · ") : ""
}

const EMPTY_FORM = (userId, projectId) => ({
  createdBy: userId,
  proyecto: projectId || "",
  task: "",
  adminNotes: { ops: [{ insert: "\n" }] },
  assignees: userId ? [userId] : [],
  limitDate: "",
  status: "Sin empezar",
  priority: "Media",
  adminLinks: [],
  adminAudios: [],
  projectTagIds: [],
})

const TaskCreateModal = () => {
  const user = useSelector(s => s.auth.user)
  const { createModalOpen, closeCreateModal, workers, selectedProject, createTask, uploadAudio, getStatusId, createModalPreselectedStatus, estadosTareas } = useTareas()

  const [form, setForm] = useState(EMPTY_FORM(user?.id, selectedProject?.id))
  const [isRecording, setIsRecording] = useState(false)
  const [newLink, setNewLink] = useState("")
  const [assignPickerOpen, setAssignPickerOpen] = useState(false)
  const assignPickerRef = useRef(null)

  useEffect(() => {
    if (createModalOpen) {
      const base = EMPTY_FORM(user?.id, selectedProject?.id)
      let status = base.status
      if (createModalPreselectedStatus && estadosTareas.some(e => e.name === createModalPreselectedStatus)) {
        status = createModalPreselectedStatus
      } else if (estadosTareas.length > 0 && !estadosTareas.some(e => e.name === base.status)) {
        const fallback = estadosTareas.find(e => e.name === "Por comenzar")?.name || estadosTareas[0].name
        status = fallback
      }
      setForm({ ...base, status, projectTagIds: [] })
    }
  }, [createModalOpen, selectedProject?.id, user?.id, createModalPreselectedStatus, estadosTareas])

  useEffect(() => {
    if (!createModalOpen) setAssignPickerOpen(false)
  }, [createModalOpen])

  useEffect(() => {
    if (!assignPickerOpen) return
    const onPointerDown = (e) => {
      if (assignPickerRef.current && !assignPickerRef.current.contains(e.target)) {
        setAssignPickerOpen(false)
      }
    }
    const onKey = (e) => {
      if (e.key === "Escape") setAssignPickerOpen(false)
    }
    document.addEventListener("pointerdown", onPointerDown, true)
    document.addEventListener("keydown", onKey)
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true)
      document.removeEventListener("keydown", onKey)
    }
  }, [assignPickerOpen])

  const mediaRecorderRef = useRef(null)
  const audioChunksRef   = useRef([])

  const close = () => {
    closeCreateModal()
    setForm(EMPTY_FORM(user?.id, selectedProject?.id))
  }

  const handleSubmit = async () => {
    if (!form.task || form.assignees.length === 0 || !form.proyecto) {
      toast.warning("Proyecto, tarea y al menos un asignado son obligatorios")
      return
    }
    try {
      let audioIds = []
      for (const file of form.adminAudios) {
        const id = await uploadAudio(file)
        audioIds.push(id)
      }
      const statusId = getStatusId(form.status)
      await createTask({
        createdBy: form.createdBy,
        proyecto: form.proyecto,
        task: form.task,
        adminNotes: form.adminNotes,
        assignees: form.assignees,
        limitDate: form.limitDate,
        status: statusId || form.status,
        priority: form.priority,
        adminLinks: form.adminLinks,
        audios: audioIds,
        ...(form.projectTagIds?.length > 0
          ? { projectTags: form.projectTagIds.map((tagId) => ({ tagId })) }
          : {}),
      })
      close()
    } catch {
      // error ya manejado en context
    }
  }

  // Links
  const addLink = () => {
    if (!newLink.trim() || form.adminLinks.length >= 5) return
    setForm(p => ({ ...p, adminLinks: [...p.adminLinks, { link: newLink.trim() }] }))
    setNewLink("")
  }
  const removeLink = (i) => setForm(p => ({ ...p, adminLinks: p.adminLinks.filter((_, idx) => idx !== i) }))

  // Audio recording
  const startRecording = () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Tu navegador no soporta grabación de audio")
      return
    }
    navigator.mediaDevices.getUserMedia({ audio: true }).then(stream => {
      const mr = new MediaRecorder(stream)
      mediaRecorderRef.current = mr
      mr.start()
      setIsRecording(true)
      mr.ondataavailable = e => audioChunksRef.current.push(e.data)
      mr.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: "audio/wav" })
        const file = new File([blob], `audio_${Date.now()}.wav`, { type: "audio/wav" })
        setIsRecording(false)
        if (file.size <= 5_000_000 && form.adminAudios.length < 5) {
          setForm(p => ({ ...p, adminAudios: [...p.adminAudios, file] }))
        } else {
          toast.warning("Audio excede 5MB o límite de 5 archivos")
        }
        audioChunksRef.current = []
      }
    }).catch(e => toast.error("Error al acceder al micrófono: " + e))
  }

  const stopRecording = () => {
    mediaRecorderRef.current?.stop()
    setIsRecording(false)
  }

  const deleteAudio = (i) => setForm(p => ({ ...p, adminAudios: p.adminAudios.filter((_, idx) => idx !== i) }))

  const toggleAssignee = (id) => {
    setForm(p => ({
      ...p,
      assignees: p.assignees.includes(id) ? p.assignees.filter(a => a !== id) : [...p.assignees, id],
    }))
  }

  const removeAssignee = (id) => {
    setForm(p => ({ ...p, assignees: p.assignees.filter(a => a !== id) }))
  }

  const toggleProjectTag = (tagId) => {
    setForm((p) => {
      const cur = p.projectTagIds || []
      const has = cur.includes(tagId)
      return {
        ...p,
        projectTagIds: has ? cur.filter((x) => x !== tagId) : [...cur, tagId],
      }
    })
  }

  const projectTagsForPicker = (selectedProject?.tags || []).filter((t) => t && !t.archived)

  const workersSortedForPicker = [...workers].sort((a, b) => {
    const aSel = form.assignees.includes(a.id)
    const bSel = form.assignees.includes(b.id)
    if (aSel !== bSel) return aSel ? -1 : 1
    return (a.name || "").localeCompare(b.name || "", "es", { sensitivity: "base" })
  })

  return (
    <Modal
      activeModal={createModalOpen}
      title="Nueva tarea"
      onClose={close}
      centered={true}
      className="max-w-4xl"
    >
      <div className="flex flex-col gap-5 text-xs max-h-[80vh] overflow-y-auto">

        <div className="grid grid-cols-2 gap-4">
          {/* Tarea */}
          <InputFormValue
            required
            title="Tarea"
            value={form.task}
            setValue={v => setForm(p => ({ ...p, task: v }))}
            icon={<FaCheck />}
            placeholder="Nombre de la tarea"
            type="text"
          />
        </div>

        {/* Notas admin - Quill */}
        <div>
          <label className="text-xs font-medium text-gray-700 block mb-1">Notas</label>
          <style>{`
            .create-notes-editor .ql-container { font-size: 12px; border-radius: 0 0 8px 8px; border-color: #E5E7EB; min-height: 80px; }
            .create-notes-editor .ql-toolbar { border-radius: 8px 8px 0 0; border-color: #E5E7EB; padding: 4px; }
            .create-notes-editor .ql-toolbar button { width: 24px; height: 24px; }
            .create-notes-editor .ql-editor { padding: 8px 10px; min-height: 80px; }
            .create-notes-editor .ql-editor.ql-blank::before { font-style: normal; color: #9CA3AF; font-size: 12px; }
          `}</style>
          <div className="create-notes-editor">
            <ReactQuill
              theme="snow"
              value={normalizeToDelta(form.adminNotes)}
              onChange={(content, delta, source, editor) => {
                if (source === "user") setForm(p => ({ ...p, adminNotes: editor.getContents() }))
              }}
              placeholder="Notas adicionales..."
              modules={{
                toolbar: [
                  ["bold", "italic", "underline"],
                  [{ list: "ordered" }, { list: "bullet" }],
                  [{ color: [] }],
                  ["link"],
                  ["clean"],
                ],
              }}
              formats={["bold", "italic", "underline", "list", "bullet", "color", "link"]}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* Asignados */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-gray-700 flex items-center gap-1.5">
              <FaUser className="text-gray-400 text-[11px]" />
              Asignados <span className="text-red-500">*</span>
            </label>
            <div className="flex flex-col gap-2">
              {form.assignees.length > 0 && (
                <div className="flex flex-wrap gap-1.5" role="list" aria-label="Asignados seleccionados">
                  {form.assignees.map(id => {
                    const w = workers.find(x => x.id === id)
                    if (!w) return null
                    const sub = workerAreaPuesto(w)
                    return (
                      <div
                        key={id}
                        role="listitem"
                        className="group inline-flex max-w-full items-center gap-1 rounded-md border border-orange-300 bg-orange-100 px-2 py-1 text-[11px] text-gray-900 shadow-sm"
                      >
                        <span className="min-w-0">
                          <span className="font-medium">{formatWorkerDisplayName(w.name || w.email) || "Sin nombre"}</span>
                          {sub ? <span className="text-gray-600"> · {sub}</span> : null}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeAssignee(id)}
                          className="flex-shrink-0 rounded p-0.5 text-orange-800/70 hover:bg-orange-200/80 hover:text-orange-900"
                          aria-label={`Quitar a ${formatWorkerDisplayName(w.name || w.email) || "Sin nombre"}`}
                        >
                          <FaTimes className="text-[9px]" />
                        </button>
                      </div>
                    )
                  })}
                </div>
              )}
              <div ref={assignPickerRef} className="relative">
                <button
                  type="button"
                  id="assign-picker-trigger"
                  aria-expanded={assignPickerOpen}
                  aria-controls="assign-picker-list"
                  onClick={() => setAssignPickerOpen(o => !o)}
                  className="flex w-full items-center justify-between gap-2 rounded-lg border border-gray-300 bg-white px-2.5 py-2 text-left text-xs text-gray-700 shadow-sm transition-colors hover:border-orange-400 hover:bg-orange-50/40 focus:outline-none focus:ring-2 focus:ring-orange-300"
                >
                  <span className="min-w-0 truncate">
                    {workers.length === 0
                      ? "No hay trabajadores disponibles"
                      : assignPickerOpen
                        ? "Ocultar lista de trabajadores"
                        : "Elegir o quitar asignados…"}
                  </span>
                  <FaChevronDown className={`flex-shrink-0 text-[10px] text-gray-500 transition-transform ${assignPickerOpen ? "rotate-180" : ""}`} />
                </button>
                {assignPickerOpen && workers.length > 0 && (
                <div
                  id="assign-picker-list"
                  role="listbox"
                  aria-multiselectable="true"
                  aria-labelledby="assign-picker-trigger"
                  className="absolute left-0 right-0 top-full z-20 mt-1 max-h-52 overflow-y-auto rounded-lg border border-gray-200 bg-white p-1.5 shadow-lg"
                >
                  {workersSortedForPicker.map(w => {
                    const selected = form.assignees.includes(w.id)
                    const sub = workerAreaPuesto(w)
                    return (
                      <button
                        key={w.id}
                        type="button"
                        role="option"
                        aria-selected={selected}
                        onClick={() => toggleAssignee(w.id)}
                        className={`mb-1 flex w-full flex-col rounded-md border px-2.5 py-2 text-left text-xs transition-colors last:mb-0 ${
                          selected
                            ? "border-orange-400 bg-orange-100 text-gray-900 shadow-sm"
                            : "border-gray-300 bg-gray-50 text-gray-800 hover:bg-gray-100"
                        }`}
                      >
                        <span className="font-semibold leading-tight">{formatWorkerDisplayName(w.name || w.email) || "Sin nombre"}</span>
                        {sub ? (
                          <span className="mt-0.5 text-[10px] leading-snug text-gray-600">{sub}</span>
                        ) : (
                          <span className="mt-0.5 text-[10px] italic text-gray-400">Sin área ni puesto</span>
                        )}
                      </button>
                    )
                  })}
                </div>
                )}
              </div>
            </div>
          </div>

          {/* Fecha límite */}
          <InputFormValue
            title="Fecha límite"
            value={form.limitDate}
            setValue={v => setForm(p => ({ ...p, limitDate: v }))}
            icon={<MdSchedule />}
            type="datetime-local"
          />
        </div>

        {projectTagsForPicker.length > 0 && (
          <div>
            <label className="text-xs font-medium text-gray-700 block mb-1.5">Tags del proyecto</label>
            <div className="flex flex-wrap gap-1.5">
              {projectTagsForPicker.map((tag) => {
                const active = (form.projectTagIds || []).includes(tag.id)
                return (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => toggleProjectTag(tag.id)}
                    className={`text-[11px] px-2 py-1 rounded-lg border transition-colors ${
                      active
                        ? "border-orange-400 bg-orange-50 text-orange-900 font-medium"
                        : "border-gray-200 text-gray-600 hover:border-gray-300 bg-white"
                    }`}
                  >
                    <span
                      className="inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle"
                      style={{ backgroundColor: tag.color || "#6B7280" }}
                    />
                    {tag.name}
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {/* Estado + Prioridad en fila */}
        <div className="grid grid-cols-2 gap-4">
          <InputFormValue
            title="Estado"
            value={form.status}
            setValue={v => setForm(p => ({ ...p, status: v }))}
            icon={<FaCheck />}
            type="select"
            options={[
              ...(estadosTareas.some(e => e.name === "Sin empezar") ? [] : [{ value: "Sin empezar", label: "Sin empezar" }]),
              ...estadosTareas.map(e => ({ value: e.name, label: e.name })),
            ]}
          />
          <InputFormValue
            title="Prioridad"
            value={form.priority}
            setValue={v => setForm(p => ({ ...p, priority: v }))}
            icon={<FaCheck />}
            type="select"
            options={PRIORITIES.map(p => ({ value: p, label: p }))}
          />
        </div>

        {/* Links */}
        <div>
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Links</h3>
          {form.adminLinks.length > 0 && (
            <div className="space-y-1.5 mb-2">
              {form.adminLinks.map((l, i) => (
                <div key={i} className="flex items-center gap-2 group">
                  <a
                    href={l.link || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 text-xs text-blue-600 hover:underline truncate"
                  >
                    {l.link || <span className="text-gray-400 italic">sin url</span>}
                  </a>
                  <button
                    onClick={() => l.link && window.open(l.link, "_blank")}
                    className="opacity-0 group-hover:opacity-100 text-blue-400 hover:text-blue-600 transition-all"
                  >
                    <FaExternalLinkAlt className="text-[10px]" />
                  </button>
                  <button
                    onClick={() => removeLink(i)}
                    className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-all"
                  >
                    <FaTrash className="text-[10px]" />
                  </button>
                </div>
              ))}
            </div>
          )}
          {form.adminLinks.length < 5 ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newLink}
                onChange={e => setNewLink(e.target.value)}
                onKeyDown={e => e.key === "Enter" && addLink()}
                placeholder="https://..."
                className="flex-1 text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-orange-300"
              />
              <button onClick={addLink} className="p-1.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600">
                <FaPlus className="text-xs" />
              </button>
            </div>
          ) : (
            <p className="text-xs text-gray-400 text-center">Máximo 5 links</p>
          )}
        </div>

        {/* Audios */}
        <div>
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Audios</h3>
          {form.adminAudios.length > 0 && (
            <div className="space-y-2 mb-3">
              {form.adminAudios.map((file, i) => {
                const url = URL.createObjectURL(file)
                return (
                  <div key={i} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-gray-700 truncate max-w-[200px]">{file.name}</span>
                      <div className="flex gap-1">
                        <button onClick={() => window.open(url, "_blank")} className="p-1 text-blue-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors">
                          <FaExternalLinkAlt className="text-[10px]" />
                        </button>
                        <button onClick={() => deleteAudio(i)} className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors">
                          <FaTrash className="text-[10px]" />
                        </button>
                      </div>
                    </div>
                    <audio controls className="w-full h-8">
                      <source src={url} type="audio/wav" />
                    </audio>
                  </div>
                )
              })}
            </div>
          )}
          {form.adminAudios.length < 5 && (
            !isRecording ? (
              <button onClick={startRecording} className="w-full flex items-center justify-center gap-2 py-2 rounded-lg border border-dashed border-gray-200 hover:border-green-300 hover:bg-green-50 text-gray-400 hover:text-green-600 transition-colors">
                <FaMicrophone className="text-xs" /> Grabar audio
              </button>
            ) : (
              <button onClick={stopRecording} className="w-full flex items-center justify-center gap-2 py-2 rounded-lg bg-red-50 border border-red-200 text-red-600 animate-pulse">
                <FaStop className="text-xs" /> Detener grabación
              </button>
            )
          )}
        </div>

        {/* Botón crear */}
        <MainSaveButton
          onClick={handleSubmit}
          fullWidth
          text="Crear Tarea"
        />
      </div>
    </Modal>
  )
}

export default TaskCreateModal
